import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors, font, type, layout, elevation } from '../theme/tokens';
import { useI18n } from '../i18n';

// ─────────────────────────────────────────────────────────────────────────────
// Shared building blocks for the prototype pages.
// Everything here is light-surface and token-driven.
// ─────────────────────────────────────────────────────────────────────────────

/** Standard panel surface. Spread into an sx prop. */
export const panel = {
  backgroundColor: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: layout.radiusLg,
  padding: '18px 20px',
  boxShadow: elevation.card,
} as const;

export type Tone = 'neutral' | 'accent' | 'ok' | 'warn' | 'alert' | 'info';

const toneMap: Record<Tone, { fg: string; bg: string; bd: string }> = {
  neutral: { fg: colors.inkMuted, bg: colors.bgAlt,      bd: colors.border },
  accent:  { fg: '#4A6310',       bg: colors.accentSoft, bd: '#B9DE7A' },
  ok:      { fg: colors.ok,       bg: colors.okSoft,     bd: '#B7D8C6' },
  warn:    { fg: colors.warn,     bg: colors.warnSoft,   bd: '#E8C68A' },
  alert:   { fg: colors.alert,    bg: colors.alertSoft,  bd: '#E7B3B0' },
  info:    { fg: colors.info,     bg: colors.infoSoft,   bd: '#B4BBFA' },
};

export const tone = (t: Tone) => toneMap[t];

// ── Labels ───────────────────────────────────────────────────────────────────

/** Small uppercase caption. */
export const Eyebrow: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color }) => (
  <Typography sx={{ ...type.eyebrow, color: color ?? colors.inkFaint }}>{children}</Typography>
);

/** Section heading with a trailing hairline. */
export const SectionLabel: React.FC<{ children: React.ReactNode; right?: React.ReactNode }> = ({ children, right }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
    <Eyebrow>{children}</Eyebrow>
    <Box sx={{ flex: 1, height: '1px', backgroundColor: colors.border }} />
    {right}
  </Box>
);

/** Header inside a panel. */
export const PanelTitle: React.FC<{ children: React.ReactNode; right?: React.ReactNode }> = ({ children, right }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1.5,
    mb: 2,
    pb: 1.5,
    borderBottom: `1px solid ${colors.border}`,
  }}>
    <Typography sx={{ ...type.eyebrow, fontSize: '11px', color: colors.inkMuted }}>
      {children}
    </Typography>
    {right}
  </Box>
);

// ── Badge ────────────────────────────────────────────────────────────────────

export const Badge: React.FC<{ label: string; t?: Tone }> = ({ label, t = 'neutral' }) => {
  const c = toneMap[t];
  return (
    <Box component="span" sx={{
      fontFamily: font.mono,
      fontSize: '10px',
      fontWeight: 500,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      px: 1.25,
      py: '4px',
      borderRadius: layout.radius,
      backgroundColor: c.bg,
      color: c.fg,
      border: `1px solid ${c.bd}`,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </Box>
  );
};

// ── Stat card ────────────────────────────────────────────────────────────────

export const StatCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  t?: Tone;
}> = ({ label, value, sub, t = 'neutral' }) => {
  const emphasised = t !== 'neutral';
  const c = toneMap[t];
  return (
    <Box sx={{
      ...panel,
      padding: '16px 18px',
      position: 'relative',
      overflow: 'hidden',
      '&::before': emphasised ? {
        content: '""',
        position: 'absolute',
        top: 0, left: 0, bottom: 0,
        width: '3px',
        backgroundColor: c.fg,
      } : undefined,
    }}>
      <Typography sx={{ ...type.eyebrow, fontSize: '10px', color: colors.inkFaint, mb: 1 }}>
        {label}
      </Typography>
      <Typography sx={{
        fontFamily: font.sans,
        fontSize: '30px',
        fontWeight: 700,
        letterSpacing: '-0.02em',
        color: emphasised ? c.fg : colors.ink,
        lineHeight: 1,
      }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontFamily: font.sans, fontSize: '12px', color: colors.inkFaint, mt: 0.75 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
};

// ── Page header ──────────────────────────────────────────────────────────────

export const PageHeader: React.FC<{
  eyebrow: string;
  title: string;
  right?: React.ReactNode;
}> = ({ eyebrow, title, right }) => (
  <Box sx={{ mb: 4 }}>
    <Box sx={{ width: 40, height: 4, backgroundColor: colors.accent, mb: 2 }} />
    <Box sx={{
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 2,
    }}>
      <Box>
        <Eyebrow>{eyebrow}</Eyebrow>
        <Typography sx={{
          ...type.display,
          fontSize: { xs: '26px', md: '32px' },
          color: colors.ink,
          mt: 1,
        }}>
          {title}
        </Typography>
      </Box>
      {right && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          {right}
        </Box>
      )}
    </Box>
  </Box>
);

// ── Footer ───────────────────────────────────────────────────────────────────

export const DemoFooter: React.FC<{ note: string }> = ({ note }) => {
  const { t } = useI18n();
  return (
    <Box sx={{
      mt: 6,
      pt: 2,
      borderTop: `1px solid ${colors.border}`,
      display: 'flex',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 1,
    }}>
      <Typography sx={{ ...type.meta, fontSize: '10px', color: colors.inkGhost }}>
        {t('common.confidential')}
      </Typography>
      <Typography sx={{ ...type.meta, fontSize: '10px', color: colors.inkGhost }}>
        {note}
      </Typography>
    </Box>
  );
};

// ── Floating tooltip (for SVG charts) ────────────────────────────────────────

export type TipState = { visible: boolean; x: number; y: number; content: string };
export const emptyTip: TipState = { visible: false, x: 0, y: 0, content: '' };

export const Tooltip: React.FC<{ tip: TipState }> = ({ tip }) =>
  !tip.visible ? null : (
    <Box sx={{
      position: 'fixed',
      left: tip.x,
      top: tip.y,
      backgroundColor: colors.dark,
      border: `1px solid ${colors.darkAlt}`,
      borderLeft: `3px solid ${colors.accent}`,
      borderRadius: layout.radius,
      px: 1.75,
      py: 1.25,
      fontFamily: font.mono,
      fontSize: '11px',
      color: colors.onDark,
      lineHeight: 1.8,
      whiteSpace: 'pre',
      pointerEvents: 'none',
      zIndex: 1400,
      boxShadow: '0 8px 28px rgba(22,25,28,0.28)',
    }}>
      {tip.content}
    </Box>
  );

// ── Meter bar ────────────────────────────────────────────────────────────────

export const Meter: React.FC<{
  label: string;
  pct: number;
  t?: Tone;
  note?: string;
  labelWidth?: number;
}> = ({ label, pct, t = 'neutral', note, labelWidth = 56 }) => {
  const c = toneMap[t];
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
        <Typography sx={{
          fontFamily: font.mono,
          fontSize: '11px',
          fontWeight: 500,
          color: colors.ink,
          width: labelWidth,
          flexShrink: 0,
        }}>
          {label}
        </Typography>
        <Box sx={{
          flex: 1,
          height: 8,
          borderRadius: '2px',
          backgroundColor: colors.bgAlt,
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
        }}>
          <Box sx={{
            width: `${Math.max(0, Math.min(100, pct))}%`,
            height: '100%',
            backgroundColor: t === 'neutral' ? colors.borderStrong : c.fg,
          }} />
        </Box>
        <Typography sx={{
          fontFamily: font.mono,
          fontSize: '11px',
          fontWeight: 500,
          color: colors.inkMuted,
          width: 38,
          textAlign: 'right',
          flexShrink: 0,
        }}>
          {pct < 20 ? '<20%' : `${pct}%`}
        </Typography>
      </Box>
      {note && (
        <Typography sx={{
          fontFamily: font.sans,
          fontSize: '11px',
          color: colors.inkFaint,
          ml: `${labelWidth + 10}px`,
        }}>
          {note}
        </Typography>
      )}
    </Box>
  );
};
